import type { WidgetConfig } from '../../infrastructure/contracts/widget.contract';

/**
 * Phase 1 carries the eleven existing dashboard screens in as COMPOSITE
 * surfaces — whole screens registered behind the new resolution, gating and
 * state contract, with zero prop re-plumbing and therefore zero risk of
 * breaking a working screen. Phase 2 splits each composite into individually
 * registered tiles; these ids and the `dataSource` field are the seam that lets
 * that happen without touching the switch again.
 *
 * Kept free of React imports on purpose: the contract below is checked by
 * `dashboard-routing.test.ts`, which must run without loading eleven pages.
 */
export interface SurfaceDefinition {
  id: string;
  title: string;
  config: Partial<WidgetConfig>;
}

const declared = (endpoint: string) => ({ dataSource: { kind: 'api' as const, endpoint } });
const unaudited = (note: string) => ({ dataSource: { kind: 'undeclared' as const, note } });

export const SURFACE_DEFINITIONS: SurfaceDefinition[] = [
  { id: 'sales.own.surface', title: 'My Dashboard', config: { level: 'own', workspace: 'sales', ...declared('/api/my/stats') } },
  { id: 'sales.department.surface', title: 'Team Dashboard', config: { level: 'department', workspace: 'sales', ...declared('/api/dashboard-kpi/team') } },
  { id: 'sales.company.surface', title: 'Company Dashboard', config: { level: 'company', ...declared('/api/dashboard-kpi/owner') } },
  {
    id: 'it.own.surface', title: 'My Dashboard',
    config: { level: 'own', ...unaudited('IT employee surface fetches through its own hooks; endpoint not audited in this change.') },
  },
  {
    id: 'it.department.surface', title: 'IT Team Dashboard',
    config: { level: 'department', ...unaudited('IT admin surface fetches through its own hooks; endpoint not audited in this change.') },
  },
  {
    id: 'marketing.own.surface', title: 'My Dashboard',
    config: { level: 'own', ...unaudited('Marketing employee surface fetches through its own hooks; endpoint not audited in this change.') },
  },
  {
    id: 'marketing.department.surface', title: 'Marketing Team Dashboard',
    config: { level: 'department', ...unaudited('Marketing admin surface fetches through its own hooks; endpoint not audited in this change.') },
  },
  { id: 'delivery.surface', title: 'Dispatch Dashboard', config: { ...declared('/api/sales-ops/orders/dispatch-queue') } },
  {
    id: 'support.surface', title: 'Support Dashboard',
    config: { ...unaudited('Support surface renders the SLA queue through its own hooks; endpoint not audited in this change.') },
  },
  {
    id: 'field.surface', title: 'Field Dashboard',
    config: { ...unaudited('Field executive surface fetches through its own hooks; endpoint not audited in this change.') },
  },
  {
    id: 'base.own.surface', title: 'My Dashboard',
    config: { ...unaudited('Operational fallback surface; endpoint not audited in this change.') },
  },
  { id: 'base.department.surface', title: 'Team Dashboard', config: { level: 'department', ...declared('/api/dashboard-kpi/team') } },
];

/**
 * The one tile that proves the contract in Phase 1: incentives have no live
 * source because `src/api-hr-incentives-sync.js` is exported but imported by
 * nothing. It renders as a named gap, not as ₹0.
 */
export const INCENTIVES_GAP = {
  id: 'sales.own.incentives',
  title: 'My Incentives',
  config: {
    level: 'own' as const,
    workspace: 'sales',
    category: 'statistics' as const,
    defaultSize: { w: 12, h: 2 },
    dataSource: {
      kind: 'none' as const,
      reason: 'The incentive engine is not connected to payment receipts — src/api-hr-incentives-sync.js has no importer.',
    },
  },
};

export const ALL_SURFACE_IDS = [...SURFACE_DEFINITIONS.map((s) => s.id), INCENTIVES_GAP.id];
